import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { IssueFilterKeys, IssueSource } from '../../../../shared/issuesV2';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../redux/savedFilters/hooks';
import FilterCardCheckboxes from '../../generic/Filter/FilterCardCheckboxes';
import FilterPanel from '../../generic/Filter/FilterPanel';
import useFetchIssuesAction from '../IssuesInbox/hooks/useFetchIssuesAction';
import type { SelectOption } from '../../../types';

const DETECTION_SOURCE_VENDORED_OPTION = { value: IssueSource.VendoredDependency, label: 'Vendored Dependency' };
const DETECTION_SOURCE_SNIPPET_OPTION = { value: IssueSource.Snippet, label: 'Snippet' };
// When vendored dependencies should not be shown - we want the label to read 'Dependency' to avoid confusion
const DETECTION_SOURCE_DEPENDENCY_OPTION = { value: IssueSource.ManagedDependency, label: 'Dependency' };
const DETECTION_SOURCE_MANAGED_OPTION = { value: IssueSource.ManagedDependency, label: 'Managed Dependency' };

/**
 * Build the issue source options based on whether vendored deps or snippets filters should be shown.
 * - When both shown: Managed Dependency, Vendored Dependency, Snippet
 * - When snippets not shown: Managed Dependency and Vendored Dependency,
 * - When vendored deps not shown: Dependency and Snippet
 */
function getIssueSourceOptions(
  shouldShowVendoredFilter: boolean,
  shouldShowSnippetFilter: boolean,
): SelectOption[] {
  if (shouldShowVendoredFilter && shouldShowSnippetFilter) {
    return [
      DETECTION_SOURCE_MANAGED_OPTION,
      DETECTION_SOURCE_VENDORED_OPTION,
      DETECTION_SOURCE_SNIPPET_OPTION,
    ];
  }

  if (shouldShowVendoredFilter) {
    return [
      DETECTION_SOURCE_MANAGED_OPTION,
      DETECTION_SOURCE_VENDORED_OPTION,
    ];
  }

  if (shouldShowSnippetFilter) {
    return [
      DETECTION_SOURCE_DEPENDENCY_OPTION,
      DETECTION_SOURCE_SNIPPET_OPTION,
    ];
  }

  // We do not expect to hit this case
  // IssueSourceFilter should be hidden when both arguments are false
  return [];
}

type Props = {
  selectedOptions?: string[];
  shouldShowVendoredFilter: boolean;
  shouldShowSnippetFilter: boolean;
};

/**
 * Multi-checkbox filter for issue source.
 * When vendored dependencies are available, will show Managed Dependency and Vendored Dependency
 * options instead of the single Dependency option.
 */
const IssueSourceFilter: VoidFunctionComponent<Props> = ({
  selectedOptions,
  shouldShowVendoredFilter,
  shouldShowSnippetFilter,
}) => {
  const setFilters = useFetchIssuesAction(IssuesV2Store.actions.setFilter);
  const stateFilters = useSelector(IssuesV2Store.selectors.getFilter);
  const setFilterHasChanged = useSetFilterHasChanged();

  const options = getIssueSourceOptions(shouldShowVendoredFilter, shouldShowSnippetFilter);

  const selectedCheckboxes = (selectedOptions ?? []).map((option: string) => ({
    label: options.find(filterOption => filterOption.value === option)?.label || option,
    value: option,
  }));

  const onIssueSourceChange = (newSelected: SelectOption[]) => {
    setFilters({
      ...stateFilters,
      [IssueFilterKeys.IssueSource]: newSelected.map(({ value }) => value as IssueSource),
    });
    setFilterHasChanged(true);
  };

  return (
    <FilterPanel>
      <FilterCardCheckboxes
        label="Issue Source"
        options={options}
        values={selectedCheckboxes}
        onChange={onIssueSourceChange}
        testId="issues-source-filters"
      />
    </FilterPanel>
  );
};

export default IssueSourceFilter;
